// Shared form validation utilities for contact forms

export interface FieldState {
  touched: boolean;
  valid: boolean | null; // null = not yet validated
}

// ─── Validators ────────────────────────────────────────────────────────────────

export function validateName(value: string): string {
  if (!value.trim()) return 'Full name is required';
  if (value.trim().length < 2) return 'Full name must be at least 2 characters';
  return '';
}

export function validatePhone(value: string): string {
  if (!value.trim()) return 'Phone number is required';
  // Strip spaces, hyphens, parentheses for digit counting
  const stripped = value.replace(/[\s\-().]/g, '');
  // Accept +44, 07, 01, 02 formats
  const ukPattern = /^(\+44|0044|0)[1-9]\d{8,9}$/;
  if (!ukPattern.test(stripped)) return 'Enter a valid UK phone number';
  return '';
}

export function validateEmail(value: string): string {
  if (!value.trim()) return 'Email address is required';
  // Must contain @ and a dot after @
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(value.trim())) return 'Enter a valid email address';
  return '';
}

export function validateRegistration(value: string): string {
  if (!value.trim()) return 'Vehicle registration is required';
  // UK registration: remove spaces, check pattern
  const stripped = value.replace(/\s/g, '').toUpperCase();
  // Modern (2001+): AB12 CDE — 2 letters, 2 digits, 3 letters
  // Prefix (1983-2001): A123 BCD — 1 letter, up to 3 digits, 3 letters
  // Suffix (1963-1983): ABC 123A — 3 letters, up to 3 digits, 1 letter
  // Dateless / cherished: various
  const modernPattern = /^[A-Z]{2}[0-9]{2}[A-Z]{3}$/;
  const prefixPattern = /^[A-Z][0-9]{1,3}[A-Z]{3}$/;
  const suffixPattern = /^[A-Z]{3}[0-9]{1,3}[A-Z]$/;
  const datelessPattern = /^[A-Z]{1,3}[0-9]{1,4}$|^[0-9]{1,4}[A-Z]{1,3}$/;
  if (
    !modernPattern.test(stripped) &&
    !prefixPattern.test(stripped) &&
    !suffixPattern.test(stripped) &&
    !datelessPattern.test(stripped)
  ) {
    return 'Enter a valid UK registration (e.g., AB12 CDE)';
  }
  return '';
}

export function validateEnquiryType(value: string): string {
  if (!value) return 'Please select an enquiry type';
  return '';
}

// ─── Email typo suggestions ─────────────────────────────────────────────────

const DOMAIN_TYPOS: Record<string, string> = {
  'gmail.cmo': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.cmo': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmali.com': 'hotmail.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.cmo': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'outloo.com': 'outlook.com',
  'outlok.com': 'outlook.com',
  'outlook.cmo': 'outlook.com',
  'iclod.com': 'icloud.com',
  'icloud.cmo': 'icloud.com',
  'icoud.com': 'icloud.com',
};

export function getEmailSuggestion(email: string): string | null {
  const atIndex = email.lastIndexOf('@');
  if (atIndex === -1) return null;
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1).toLowerCase();
  const correction = DOMAIN_TYPOS[domain];
  if (correction) return `${local}@${correction}`;
  return null;
}

// ─── Validate all fields at once (for submit) ──────────────────────────────

export interface FormValues {
  name: string;
  phone: string;
  email: string;
  registration: string;
  enquiryType: string;
  vehicleMake?: string;
  message?: string;
  gdpr?: boolean;
}

export function validateAll(values: FormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  const nameErr = validateName(values.name);
  if (nameErr) errors.name = nameErr;
  const phoneErr = validatePhone(values.phone);
  if (phoneErr) errors.phone = phoneErr;
  const emailErr = validateEmail(values.email);
  if (emailErr) errors.email = emailErr;
  const regErr = validateRegistration(values.registration);
  if (regErr) errors.registration = regErr;
  const enquiryErr = validateEnquiryType(values.enquiryType);
  if (enquiryErr) errors.enquiryType = enquiryErr;
  if (values.gdpr === false) errors.gdpr = 'You must consent to proceed';
  return errors;
}
